import React, { useEffect, useRef, useState } from "react";
import {
  motion,
  useScroll,
  useTransform,
  useReducedMotion,
  AnimatePresence,
} from "framer-motion";
import Lenis from "lenis";
import SwitchControl from "../components/site/SwitchControl";

const EASE = [0.85, 0, 0.15, 1];

/* =========================================================== Primitives */

const Eyebrow = ({ children, dark, className = "" }) => (
  <p
    className={`font-body text-xs font-medium uppercase tracking-[0.24em] ${className}`}
    style={{ color: dark ? "rgba(245,242,234,0.5)" : "rgba(40,40,40,0.5)" }}
  >
    {children}
  </p>
);

/* Sharp offset-shadow CTA, rounded-none, lifts + reveals a lime block on hover */
const Cta = ({ children, primary, dark, testId }) => {
  const bg = primary ? "#5f8f10" : "transparent";
  const fg = primary ? "#282828" : dark ? "#F5F2EA" : "#282828";
  const bd = primary ? "#5f8f10" : dark ? "rgba(245,242,234,0.35)" : "#282828";
  return (
    <button
      data-testid={testId}
      className="font-body group inline-flex items-center gap-3 rounded-none border-2 px-8 py-5 text-xs font-medium uppercase tracking-[0.18em] transition-transform duration-300 ease-cinema hover:-translate-x-1 hover:-translate-y-1"
      style={{
        backgroundColor: bg,
        color: fg,
        borderColor: bd,
        boxShadow: "0 0 0 0 #5f8f10",
      }}
      onMouseEnter={(e) => (e.currentTarget.style.boxShadow = "8px 8px 0 0 #5f8f10")}
      onMouseLeave={(e) => (e.currentTarget.style.boxShadow = "0 0 0 0 #5f8f10")}
    >
      {children}
      <span className="transition-transform duration-300 ease-cinema group-hover:translate-x-1">→</span>
    </button>
  );
};

/* Background character layer, technical grid + grain + optional lime glow */
const Texture = ({ dark, glow = 0.45 }) => (
  <>
    <div
      className={`pointer-events-none absolute inset-0 z-0 ${dark ? "bg-grid-dark" : "bg-grid-light"}`}
    />
    <div
      className="pointer-events-none absolute inset-0 z-0"
      style={{
        opacity: glow,
        background: "radial-gradient(circle at 82% 34%, rgba(155,224,23,0.18), transparent 52%)",
      }}
    />
    <div className="grain pointer-events-none absolute inset-0 z-0" />
  </>
);

/* -------------------------------------------------- Header */
const NAV = ["What we solve", "How we work", "Our work", "Insights"];

const ProtoHeader = () => {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const onScroll = () => setShow(window.scrollY > window.innerHeight * 0.5);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <motion.header
      data-testid="proto-header"
      initial={false}
      animate={{ y: show ? 0 : -96, opacity: show ? 1 : 0 }}
      transition={{ duration: 0.4, ease: EASE }}
      className="fixed inset-x-0 top-0 z-[90] border-b-2 border-charcoal bg-warm-white/90 backdrop-blur-md"
    >
      <div className="mx-auto flex max-w-[1360px] items-center justify-between px-8 py-4 md:px-16 lg:px-24">
        <span className="font-display text-xl uppercase tracking-tight text-charcoal" data-testid="proto-header-wordmark">
          SWITCH
        </span>
        <nav className="hidden items-center gap-9 md:flex">
          {NAV.map((n) => (
            <a
              key={n}
              href="#"
              className="font-body text-xs font-medium uppercase tracking-[0.16em] text-charcoal/70 transition-colors hover:text-charcoal"
            >
              {n}
            </a>
          ))}
        </nav>
        <button
          data-testid="proto-header-cta"
          className="font-body rounded-none border-2 border-charcoal px-5 py-2.5 text-xs font-medium uppercase tracking-[0.16em] text-charcoal transition-transform duration-300 ease-cinema hover:-translate-x-0.5 hover:-translate-y-0.5"
          onMouseEnter={(e) => (e.currentTarget.style.boxShadow = "5px 5px 0 0 #5f8f10")}
          onMouseLeave={(e) => (e.currentTarget.style.boxShadow = "0 0 0 0 #5f8f10")}
        >
          Bring Us Your Growth Problem
        </button>
      </div>
    </motion.header>
  );
};

/* -------------------------------------------------------------- Splash */
const Splash = ({ onEnter }) => {
  const reduce = useReducedMotion();
  const [on, setOn] = useState(false);
  const t = useRef();
  useEffect(() => () => clearTimeout(t.current), []);
  const flip = () => {
    if (on) return;
    setOn(true);
    t.current = setTimeout(onEnter, reduce ? 150 : 900);
  };
  return (
    <motion.div
      data-testid="proto-splash"
      className="fixed inset-0 z-[100] flex flex-col justify-between overflow-hidden bg-charcoal px-8 py-10 md:px-16 md:py-14 lg:px-24"
      initial={{ y: 0 }}
      exit={{ y: "-100%" }}
      transition={{ duration: reduce ? 0 : 1, ease: EASE }}
    >
      <Texture dark glow={0.5} />

      <div className="relative z-10 flex items-center justify-between border-b-2 border-warm-white/15 pb-6">
        <span className="font-body text-xs font-medium uppercase tracking-[0.24em] text-warm-white/50">
          SWITCH
        </span>
        <span className="font-body text-xs font-medium uppercase tracking-[0.24em] text-warm-white/50">
          Growth, switched on
        </span>
      </div>

      <div className="relative z-10 flex flex-1 flex-col items-start justify-center">
        <motion.h1
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: reduce ? 0 : 0.9, ease: EASE }}
          className="font-display text-6xl leading-[1.12] tracking-tight sm:text-7xl md:text-8xl"
          style={{ color: "#F5F2EA" }}
        >
          Everyone is
          <br />
          working.
          <span className="mt-6 flex flex-wrap items-baseline gap-x-6 gap-y-4 md:mt-10">
            <span style={{ color: "rgba(245,242,234,0.35)" }}>Growth isn&rsquo;t.</span>
            <span
              className="relative inline-flex"
              data-testid="proto-splash-switch"
            >
              {!on && !reduce && (
                <span className="pointer-events-none absolute -inset-2 animate-ping rounded-full bg-lime/20" />
              )}
              <SwitchControl active={on} onToggle={flip} size="lg" tone="dark" testId="proto-splash-toggle" />
            </span>
            <motion.span
              className="font-body text-base font-light tracking-normal"
              animate={reduce ? {} : { opacity: on ? 0 : 1 }}
            >
              <span className="font-medium uppercase tracking-[0.16em] text-warm-white/85">Flip the switch.</span>{" "}
              <span className="text-warm-white/45">Turn growth on.</span>
            </motion.span>
          </span>
        </motion.h1>
      </div>

      <div className="relative z-10 h-px w-full bg-warm-white/10" />
    </motion.div>
  );
};

/* ---------------------------------------------------------- Hero chart */
const HeroFrame = ({ active }) => {
  const bars = [
    { x: 20, offY: 40, h: 70 },
    { x: 96, offY: -30, h: 110 },
    { x: 172, offY: 60, h: 150 },
    { x: 248, offY: -10, h: 200 },
    { x: 324, offY: 30, h: 250 },
  ];
  return (
    <svg viewBox="0 0 400 320" className="h-full w-full">
      <motion.path
        d="M45 250 L121 210 L197 160 L273 100 L349 40"
        fill="none"
        stroke="#5f8f10"
        strokeWidth="3"
        strokeLinecap="square"
        initial={false}
        animate={{ pathLength: active ? 1 : 0, opacity: active ? 1 : 0 }}
        transition={{ duration: 0.9, ease: EASE }}
      />
      {bars.map((b, i) => {
        const baseY = 280 - b.h;
        return (
          <motion.g
            key={i}
            initial={false}
            animate={{ y: active ? 0 : b.offY, rotate: active ? 0 : i % 2 ? 4 : -4 }}
            transition={{ duration: 0.8, delay: active ? i * 0.05 : 0, ease: EASE }}
            style={{ originX: `${b.x + 26}px`, originY: "280px" }}
          >
            <rect
              x={b.x}
              y={baseY}
              width="52"
              height={b.h}
              fill={active ? "#F5F2EA" : "#282828"}
              opacity={active ? 1 : 0.5}
            />
            <motion.rect
              x={b.x + 20}
              y={baseY - 12}
              width="12"
              height="12"
              fill="#5f8f10"
              initial={false}
              animate={{ scale: active ? 1 : 0, opacity: active ? 1 : 0 }}
              transition={{ duration: 0.4, delay: active ? 0.35 + i * 0.05 : 0 }}
              style={{ originX: `${b.x + 26}px`, originY: `${baseY - 6}px` }}
            />
          </motion.g>
        );
      })}
    </svg>
  );
};

const Hero = ({ startActive = false }) => {
  const [active, setActive] = useState(startActive);
  const reduce = useReducedMotion();
  const fg = active ? "#F5F2EA" : "#282828";
  const sub = active ? "rgba(245,242,234,0.55)" : "rgba(40,40,40,0.55)";
  const line = active ? "rgba(245,242,234,0.2)" : "rgba(40,40,40,0.18)";
  return (
    <section
      data-testid="proto-hero"
      className="relative min-h-screen overflow-hidden transition-colors duration-700 ease-cinema"
      style={{ backgroundColor: active ? "#282828" : "#F5F2EA" }}
    >
      <Texture dark={active} glow={active ? 1 : 0.4} />

      <div className="relative z-10 mx-auto grid min-h-screen w-full max-w-[1360px] items-center gap-y-16 px-8 py-40 md:px-16 lg:grid-cols-12 lg:gap-12 lg:px-24">
        <div className="lg:col-span-7">
          <Eyebrow dark={active}>{active ? "State, Working as one" : "State, Working apart"}</Eyebrow>

          <h1
            className="font-display mt-8 text-5xl leading-[1.0] tracking-tight sm:text-6xl md:text-7xl lg:text-8xl"
            style={{ color: fg }}
          >
            <AnimatePresence mode="wait">
              {active ? (
                <motion.span key="on" initial={reduce ? {} : { opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={reduce ? {} : { opacity: 0, y: -20 }} transition={{ duration: 0.5, ease: EASE }} className="block">
                  Strategy, built
                  <br />
                  to be <span className="text-lime">executed.</span>
                </motion.span>
              ) : (
                <motion.span key="off" initial={reduce ? {} : { opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={reduce ? {} : { opacity: 0, y: -20 }} transition={{ duration: 0.5, ease: EASE }} className="block">
                  Everyone is
                  <br />
                  working. <span className="italic">Growth isn&rsquo;t.</span>
                </motion.span>
              )}
            </AnimatePresence>
          </h1>

          {/* switch aligned to the baseline of its label, not centered */}
          <div className="mt-14 flex items-baseline gap-6">
            <span className="relative -mb-2 inline-flex self-end">
              <SwitchControl active={active} onToggle={setActive} size="lg" tone={active ? "dark" : "light"} ariaLabel="Switch from working apart to working as one" testId="proto-hero-switch" />
            </span>
            <span className="font-body text-sm font-light transition-colors duration-500" style={{ color: sub }}>
              {active ? "One system. One trajectory." : "Flip to align the system"}
            </span>
          </div>

          <div className="mt-16 flex flex-wrap items-end gap-6">
            <Cta primary dark={active} testId="proto-hero-cta">Bring Us Your Growth Problem</Cta>
            <Cta dark={active} testId="proto-hero-cta-2">Find Your Switch</Cta>
          </div>
        </div>

        <div className="lg:col-span-5">
          <div className="mx-auto max-w-[460px] border-l-2 pl-8" style={{ borderColor: line }}>
            <HeroFrame active={active} />
          </div>
        </div>
      </div>

      <div className="absolute bottom-10 left-8 md:left-16 lg:left-24">
        <span className="font-body text-[11px] uppercase tracking-[0.24em]" style={{ color: sub }}>Scroll ↓</span>
      </div>
    </section>
  );
};

/* --------------------------------------------------------- Signals band */
const SIGNALS = [
  {
    k: "01",
    title: "Effort without direction",
    body: "Every team is busy. None of it compounds into growth.",
    Icon: () => (
      <svg viewBox="0 0 64 64" className="h-14 w-14" fill="none" stroke="currentColor" strokeWidth="2.5">
        <path d="M8 48 L24 20 L40 40 L56 12" strokeLinecap="square" />
        <path d="M8 56 L56 56" opacity="0.3" />
      </svg>
    ),
  },
  {
    k: "02",
    title: "Strategy that stalls",
    body: "The plan looks right on paper and dies in execution.",
    Icon: () => (
      <svg viewBox="0 0 64 64" className="h-14 w-14" fill="none" stroke="currentColor" strokeWidth="2.5">
        <rect x="10" y="10" width="18" height="18" />
        <rect x="36" y="36" width="18" height="18" />
        <path d="M28 19 L36 19" opacity="0.3" strokeDasharray="3 4" />
      </svg>
    ),
  },
  {
    k: "03",
    title: "Functions pulling apart",
    body: "Marketing, sales, product, ops, six directions, one problem.",
    Icon: () => (
      <svg viewBox="0 0 64 64" className="h-14 w-14" fill="none" stroke="currentColor" strokeWidth="2.5">
        <path d="M32 32 L10 12 M32 32 L54 12 M32 32 L10 52 M32 32 L54 52" strokeLinecap="square" />
        <rect x="27" y="27" width="10" height="10" fill="currentColor" stroke="none" />
      </svg>
    ),
  },
];

const Signals = () => (
  <section data-testid="proto-signals" className="relative border-t-2 border-charcoal bg-warm-white">
    <div className="mx-auto max-w-[1360px] px-8 py-32 md:px-16 md:py-40 lg:px-24">
      <div className="grid gap-12 lg:grid-cols-12 lg:gap-12">
        <div className="lg:col-span-4">
          <Eyebrow>What we solve</Eyebrow>
          <h2 className="font-display mt-8 text-4xl leading-[0.95] tracking-tight text-charcoal md:text-5xl lg:text-6xl">
            The signs
            <br />
            growth has
            <br />
            <span className="text-deep-green">stalled.</span>
          </h2>
        </div>

        <div className="lg:col-span-8">
          <div className="grid gap-0 border-l-2 border-charcoal">
            {SIGNALS.map((s, i) => (
              <div
                key={s.k}
                className={`group flex items-baseline gap-8 py-10 pl-10 transition-colors duration-300 hover:bg-charcoal ${i !== SIGNALS.length - 1 ? "border-b-2 border-charcoal/15" : ""}`}
              >
                <span className="font-display text-2xl text-charcoal/30 transition-colors group-hover:text-lime md:text-3xl">
                  {s.k}
                </span>
                <div className="self-center text-charcoal transition-colors duration-300 group-hover:text-lime">
                  <s.Icon />
                </div>
                <div className="flex-1">
                  <h3 className="font-display text-2xl uppercase tracking-tight text-charcoal transition-colors duration-300 group-hover:text-warm-white md:text-3xl">
                    {s.title}
                  </h3>
                  <p className="font-body mt-3 max-w-md text-base font-light text-charcoal/60 transition-colors duration-300 group-hover:text-warm-white/60">
                    {s.body}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </section>
);

/* -------------------------------------------------- Scroll model */
const NODES = [
  { label: "Marketing", ox: -330, oy: -150 },
  { label: "Sales", ox: 320, oy: -180 },
  { label: "Product", ox: -360, oy: 70 },
  { label: "Technology", ox: 360, oy: 100 },
  { label: "Operations", ox: -150, oy: 190 },
  { label: "Finance", ox: 190, oy: 200 },
];

const NodeItem = ({ progress, node, reduce }) => {
  const x = useTransform(progress, [0.05, 0.42], [reduce ? 0 : node.ox, 0]);
  const y = useTransform(progress, [0.05, 0.42], [reduce ? 0 : node.oy, 0]);
  const opacity = useTransform(progress, [0.3, 0.46], [1, 0]);
  return (
    <div className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
      <motion.div
        style={{ x, y, opacity }}
        className="font-body whitespace-nowrap border-2 border-charcoal bg-warm-white px-5 py-2.5 text-sm font-medium uppercase tracking-[0.12em] text-charcoal"
      >
        {node.label}
      </motion.div>
    </div>
  );
};

const SwitchModel = () => {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });

  const blockScale = useTransform(scrollYProgress, [0.42, 0.6], [reduce ? 1 : 0.5, 1]);
  const blockOpacity = useTransform(scrollYProgress, [0.42, 0.55], [reduce ? 1 : 0, 1]);
  const lineScale = useTransform(scrollYProgress, [0.6, 0.9], [reduce ? 1 : 0, 1]);
  const execOpacity = useTransform(scrollYProgress, [0.75, 0.95], [reduce ? 1 : 0, 1]);
  const capA = useTransform(scrollYProgress, [0, 0.28, 0.38], [1, 1, 0]);
  const capB = useTransform(scrollYProgress, [0.45, 0.55, 0.62, 0.7], [0, 1, 1, 0]);
  const capC = useTransform(scrollYProgress, [0.78, 0.9], [0, 1]);

  return (
    <section ref={ref} data-testid="proto-model" className="relative h-[260vh] border-t-2 border-charcoal bg-warm-white">
      <div className="sticky top-0 flex h-screen flex-col items-center justify-center overflow-hidden px-8">
        <Texture dark={false} glow={0.3} />

        <div className="absolute left-8 top-28 z-10 md:left-16 lg:left-24">
          <Eyebrow>The SWITCH model</Eyebrow>
        </div>

        {/* captions */}
        <div className="absolute left-8 top-1/2 z-10 max-w-sm -translate-y-1/2 md:left-16 lg:left-24">
          <motion.p style={{ opacity: capA }} className="font-display absolute text-3xl uppercase leading-[0.95] tracking-tight text-charcoal md:text-4xl">
            Every function<br />is working.<br /><span className="text-charcoal/40">Just not together.</span>
          </motion.p>
          <motion.p style={{ opacity: capB }} className="font-display absolute text-3xl uppercase leading-[0.95] tracking-tight text-charcoal md:text-4xl">
            We reorganise them<br />around one<br /><span className="text-deep-green">growth problem.</span>
          </motion.p>
          <motion.p style={{ opacity: capC }} className="font-display absolute text-3xl uppercase leading-[0.95] tracking-tight text-charcoal md:text-4xl">
            Then build one<br />path to<br /><span className="text-deep-green">execution.</span>
          </motion.p>
        </div>

        {/* stage */}
        <div className="relative z-10 h-[70vh] w-full max-w-3xl">
          {NODES.map((n) => (
            <NodeItem key={n.label} progress={scrollYProgress} node={n} reduce={reduce} />
          ))}

          <motion.div
            style={{ scale: blockScale, opacity: blockOpacity }}
            className="absolute left-1/2 top-1/2 flex h-44 w-44 -translate-x-1/2 -translate-y-1/2 items-center justify-center border-2 border-lime bg-charcoal p-4 text-center md:h-52 md:w-52"
          >
            <span className="font-display text-xl uppercase leading-[0.95] tracking-tight text-warm-white md:text-2xl">
              One growth<br />problem
            </span>
          </motion.div>

          <motion.div
            style={{ scaleY: lineScale }}
            className="absolute left-1/2 top-1/2 h-[26vh] w-[3px] origin-top -translate-x-1/2 bg-lime"
          />
          <motion.div
            style={{ opacity: execOpacity }}
            className="absolute left-1/2 top-[calc(50%+26vh)] -translate-x-1/2"
          >
            <div className="font-body whitespace-nowrap border-2 border-charcoal bg-lime px-6 py-3 text-sm font-medium uppercase tracking-[0.14em] text-charcoal">
              Execution
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

/* -------------------------------------------------- Closing */
const Closing = () => (
  <section data-testid="proto-closing" className="relative overflow-hidden border-t-2 border-lime bg-charcoal">
    <Texture dark glow={0.6} />
    <div className="relative z-10 mx-auto grid max-w-[1360px] gap-16 px-8 py-40 md:px-16 lg:grid-cols-12 lg:px-24">
      <div className="lg:col-span-8">
        <Eyebrow dark>Start the conversation</Eyebrow>
        <h2 className="font-display mt-8 text-5xl leading-[0.95] tracking-tight text-warm-white md:text-6xl lg:text-7xl">
          Bring us your
          <br />
          growth problem.
          <br />
          <span className="text-lime">We&rsquo;ll flip the switch.</span>
        </h2>
        <p className="font-body mt-10 max-w-lg text-lg font-light text-warm-white/55">
          Not another deck. A system that moves, strategy built around execution, and a team that stays to make it happen.
        </p>
        <div className="mt-16 flex flex-wrap items-end gap-6">
          <Cta primary dark testId="proto-closing-cta">Bring Us Your Growth Problem</Cta>
          <Cta dark testId="proto-closing-cta-2">Find Your Switch</Cta>
        </div>
      </div>
      <div className="hidden lg:col-span-4 lg:flex lg:items-center lg:justify-end">
        <SwitchControl active size="xl" tone="dark" ariaLabel="SWITCH" testId="proto-closing-switch" />
      </div>
    </div>
  </section>
);

/* -------------------------------------------------- Page */
export default function Prototype() {
  const reduce = useReducedMotion();
  const [splash, setSplash] = useState(true);

  useEffect(() => {
    if (reduce) return;
    const lenis = new Lenis({ lerp: 0.09 });
    let id;
    const raf = (t) => {
      lenis.raf(t);
      id = requestAnimationFrame(raf);
    };
    id = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(id);
      lenis.destroy();
    };
  }, [reduce]);

  return (
    <div className="font-body bg-warm-white">
      <AnimatePresence>
        {splash && <Splash key="s" onEnter={() => setSplash(false)} />}
      </AnimatePresence>
      <ProtoHeader />
      <Hero startActive />
      <Signals />
      <SwitchModel />
      <Closing />
    </div>
  );
}
